import { useEffect } from 'react';
import { apiGet } from 'Shared/api';
import authHelper from 'Utils/Helper/AuthHelpers';

/**
 * Per-partner feature switches set by operations (e.g. analytics_enabled). The login response
 * carries them as data.features and they are kept on the stored user as user.features; sessions
 * started before a switch changed are refreshed from GET /partner/features when the shell loads.
 */
export const hasFeature = (user, key) => !!(user && user.features && user.features[key]);

/** Drops nav items whose `feature` is not switched on for the user. */
export const filterNav = (nav, user) =>
  nav
    .map((g) => ({ ...g, items: g.items.filter((i) => !i.feature || hasFeature(user, i.feature)) }))
    .filter((g) => g.items.length > 0);

/** Stores features on the user in context and in storage. */
export const saveFeatures = (user, features, setCurrentAdmin) => {
  if (!user) return;
  const next = { ...user, features: { ...(user.features || {}), ...(features || {}) } };
  try {
    authHelper.setUserInfo(next);
  } catch (e) {
    /* storage unavailable: keep it in memory */
  }
  setCurrentAdmin(next);
};

/** Refreshes a partner's feature switches once per page load. Failures keep what is stored. */
export function useRefreshPartnerFeatures(user, isPartner, setCurrentAdmin) {
  const userId = user?.id;
  useEffect(() => {
    if (!isPartner || !userId) return undefined;
    let alive = true;
    apiGet('/partner/features')
      .then((res) => {
        const features = res?.data;
        if (!alive || !features || typeof features !== 'object') return;
        const current = authHelper.getUserInfo() || user;
        const changed = Object.keys(features).some((k) => !!features[k] !== hasFeature(current, k));
        if (changed || !current.features) saveFeatures(current, features, setCurrentAdmin);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isPartner]);
}
